import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, Statistic, Typography, List, Tag, Space, Empty, Progress, Spin, Avatar, Button,
} from 'antd';
import {
  TeamOutlined, FireOutlined, PhoneOutlined, RiseOutlined, ClockCircleOutlined,
  WarningOutlined, CheckCircleOutlined, CalendarOutlined, TrophyOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchLeadStats } from '../services/leadService';
import { fetchCallDashboard } from '../services/callService';
import { fetchCalendarDashboard } from '../services/calendarService';
import { PRIORITY_COLORS } from '../leads/leadConstants';
import { TYPE_COLORS } from '../calls/callConstants';

const { Title, Text } = Typography;
const BRAND = '#e27e56';

const Dashboard = () => {
  const navigate = useNavigate();
  const [lead, setLead] = useState({});
  const [call, setCall] = useState({});
  const [cal, setCal] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [l, c, ca] = await Promise.all([fetchLeadStats(), fetchCallDashboard(), fetchCalendarDashboard()]);
        setLead(l); setCall(c.counters || {}); setCal(ca);
        window.__cb = c;
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const callRaw = window.__cb || {};

  const card = (title, value, color, icon, to) => (
    <Col xs={12} sm={8} md={6} lg={4} key={title}>
      <Card bordered={false} hoverable style={{ borderRadius: 8 }} bodyStyle={{ padding: '14px 16px' }}
        onClick={to ? () => navigate(to) : undefined}>
        <Statistic title={<span style={{ color }}>{icon} {title}</span>} value={value || 0} valueStyle={{ color, fontSize: 22, fontWeight: 600 }} />
      </Card>
    </Col>
  );

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  const conv = Number(lead.conversionRate) || 0;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, padding: '4px 8px' }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Dashboard</Title>
          <Text type="secondary">Overview of leads, calls &amp; schedule</Text>
        </Col>
        <Col><Space>
          <Button icon={<CalendarOutlined />} onClick={() => navigate('/calendar')}>Calendar</Button>
          <Button type="primary" icon={<PhoneOutlined />} onClick={() => navigate('/calls/new')}>New Follow-up</Button>
        </Space></Col>
      </Row>

      <Title level={5} style={{ margin: '0 8px 8px' }}>Leads</Title>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {card('Total Leads', lead.totalLeads, BRAND, <TeamOutlined />, '/leads')}
        {card('New', lead.newLeads, '#1890ff', <RiseOutlined />, '/leads')}
        {card('Hot', lead.hot, '#f5222d', <FireOutlined />, '/leads')}
        {card('Warm', lead.warm, '#fa8c16', <FireOutlined />, '/leads')}
        {card('Cold', lead.cold, '#1890ff', <FireOutlined />, '/leads')}
        {card('Converted', lead.converted, '#52c41a', <CheckCircleOutlined />, '/leads')}
        {card('Lost', lead.lost, '#8c8c8c', <WarningOutlined />, '/leads')}
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card bordered={false} style={{ borderRadius: 8 }} bodyStyle={{ padding: '10px 16px' }}>
            <Text type="secondary">Conversion</Text>
            <Progress percent={conv} strokeColor="#52c41a" />
          </Card>
        </Col>
      </Row>

      <Title level={5} style={{ margin: '0 8px 8px' }}>Calls &amp; Follow-ups</Title>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {card("Today's Calls", call.todayCalls, '#1890ff', <PhoneOutlined />, '/calls/history')}
        {card('Pending', call.todayPending, '#fa8c16', <ClockCircleOutlined />, '/calls/today')}
        {card('Completed', call.completedToday, '#52c41a', <CheckCircleOutlined />, '/calls/completed')}
        {card('Overdue', call.overdue, '#f5222d', <WarningOutlined />, '/calls/overdue')}
        {card('Upcoming', call.upcoming, '#722ed1', <RiseOutlined />, '/calls/upcoming')}
        {card('Site Visits', cal.siteVisitsToday, '#52c41a', <CalendarOutlined />, '/calendar')}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={<span><ClockCircleOutlined /> Today's Follow-ups</span>} bordered={false} style={{ borderRadius: 8 }}
            extra={<Button type="link" onClick={() => navigate('/calls/today')}>View all</Button>}>
            {(callRaw.todayList || []).length === 0 ? <Empty description="Nothing scheduled today" /> : (
              <List dataSource={(callRaw.todayList || []).slice(0, 6)} renderItem={(it) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar style={{ background: TYPE_COLORS[it.FollowupType] || '#1890ff' }}>{it.ScheduledAt ? dayjs(it.ScheduledAt).format('HH') : '•'}</Avatar>}
                    title={<Space><Text strong>{it.ScheduledAt ? dayjs(it.ScheduledAt).format('hh:mm A') : ''}</Text><Text>{it.CustomerName}</Text>{it.Priority && <Tag color={PRIORITY_COLORS[it.Priority] || 'default'}>{it.Priority}</Tag>}</Space>}
                    description={<Space size={6}>{it.City && <span>{it.City}</span>}{it.FollowupType && <Tag color={TYPE_COLORS[it.FollowupType] || 'blue'}>{it.FollowupType}</Tag>}</Space>}
                  />
                </List.Item>
              )} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<span><RiseOutlined /> Lead Source Analytics</span>} bordered={false} style={{ borderRadius: 8, marginBottom: 16 }}>
            {(lead.bySource || []).length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" /> : (
              <Space direction="vertical" style={{ width: '100%' }}>
                {lead.bySource.slice(0, 6).map(s => {
                  const max = Math.max(1, ...lead.bySource.map(x => Number(x.n)));
                  return (
                    <div key={s.Source}>
                      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                        <Text>{s.Source}</Text><Text type="secondary">{s.n}</Text>
                      </Space>
                      <Progress percent={Math.round((Number(s.n) / max) * 100)} showInfo={false} strokeColor={BRAND} />
                    </div>
                  );
                })}
              </Space>
            )}
          </Card>
          <Card title={<span><TrophyOutlined /> Executive Leaderboard</span>} bordered={false} style={{ borderRadius: 8 }}>
            {(callRaw.leaderboard || []).length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" /> : (
              <List size="small" dataSource={callRaw.leaderboard} renderItem={(e, i) => (
                <List.Item>
                  <List.Item.Meta avatar={<Avatar style={{ background: i === 0 ? '#faad14' : '#d9d9d9' }}>{i + 1}</Avatar>}
                    title={e.ExecutiveName} description={`${e.calls} calls · ${e.converted} converted`} />
                </List.Item>
              )} />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
